/**
 * Printing through a BOB Print Relay (task 3490, Mark 2026-10-27): every report and every label BOB
 * prints is asked of the API for a printer on BOB's list, and the site's relay prints it. The
 * browser's own print dialog is only the fallback, for one job, when no relay reaches the printer.
 *
 * The rules a Print button follows, with no Vue and no fetch, so they are tested on their own
 * (tests/unit/printRelay.test.ts): which printers are offered, which is picked first, when the
 * fallback is offered, how a job is followed and the words for each outcome.
 *
 * Each user's **Printing** (Mark, 2026-10-27; the user's window) says which ways they have: the
 * site's printers through a relay, this device's own print, or both.
 */
import type { PrinterAvailabilityDto, PrintJobKind, PrintJobStateDto } from '~/types/restApiTypes'
import type { PrinterKind } from '~/stores/printerStore'
import type { PrintPath } from '~/utils/tirPrint'

/** A printer as a Print button offers it (GET api/printers/availability). */
export type PrintChoice = {
  id: number
  name: string
  kind: PrinterKind
  areaId: number | null
  area: string | null
  /** An approved relay, online, reached it in the last two minutes. */
  relayOnline: boolean
  lastReachedAt: string | null
}

/** The availability answer as the screens use it: every field there. */
export function toPrintChoice(p: PrinterAvailabilityDto): PrintChoice {
  return {
    id: p.id ?? 0,
    name: p.name ?? '',
    kind: (p.kind ?? 'Paper') as PrinterKind,
    areaId: p.areaId ?? null,
    area: p.area ?? null,
    relayOnline: !!p.relayOnline,
    lastReachedAt: p.lastReachedAt ?? null,
  }
}

/** A label's ZPL goes to a label printer; a page (HTML or PDF) to a paper one. */
export function printerKindFor(kind: PrintJobKind): PrinterKind {
  return kind === 'Zpl' ? 'Label' : 'Paper'
}

/**
 * A page for a paper printer (`Html`) is made a PDF by BOB's server (Mark, 2026-10-28): the print
 * window's Preview shows that PDF, and Use Local Printer prints it. A PDF already made (the TIR)
 * previews as itself (Mark, 2026-10-28: "go to the individual TIR, preview, and print it"). A
 * label's ZPL has no preview: it is the label.
 */
export function previewsAsPdf(kind: PrintJobKind): boolean {
  return kind === 'Html' || kind === 'Pdf'
}

/**
 * How Use Local Printer prints a job on this device: a page as the server's PDF from a hidden
 * frame where the browser shows a PDF in one (`printPath` 'frame', utils/tirPrint.ts), else the
 * job's own way, as before. Chrome on Android (the clerks' Samsungs) prints nothing from a frame, so
 * a page there is still printed from BOB's own page into Android's print sheet. A label and a PDF
 * already made (the TIR, which prints from a frame or the share sheet itself) go their own way.
 */
export function localPrintOf(kind: PrintJobKind, path: PrintPath): 'pdf' | 'own' {
  return kind === 'Html' && path === 'frame' ? 'pdf' : 'own'
}

/** The printers of one kind, in the API's order (by area, then name). */
export function printersOfKind(all: PrintChoice[], kind: PrinterKind): PrintChoice[] {
  return all.filter(p => p.kind === kind)
}

/** Where the last printer used is kept, one per kind per browser. The label key is the one the label window used before. */
export const REMEMBERED_KEYS: Record<PrinterKind, string> = {
  Label: 'bob.labelPrinter',
  Paper: 'bob.paperPrinter',
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

const browserStorage = (): StorageLike | null => {
  try { return typeof localStorage === 'undefined' ? null : localStorage } catch { return null }
}

/** The printer last used for this kind on this browser; null when none, or storage is closed. */
export function rememberedPrinter(kind: PrinterKind, storage: StorageLike | null = browserStorage()): number | null {
  try {
    const id = Number(storage?.getItem(REMEMBERED_KEYS[kind]))
    return Number.isInteger(id) && id > 0 ? id : null
  } catch {
    return null
  }
}

/** Keeps the printer used, so the next Print offers it first. A closed storage just asks again next time. */
export function rememberPrinter(kind: PrinterKind, id: number, storage: StorageLike | null = browserStorage()): void {
  try { storage?.setItem(REMEMBERED_KEYS[kind], String(id)) } catch { /* the next print asks again */ }
}

/**
 * The printer offered first: the one used last on this browser while it is still on the list; else
 * the only one; else the only one a relay reaches; else none, and the person picks.
 */
export function pickPrinter(choices: PrintChoice[], remembered: number | null): PrintChoice | null {
  const last = remembered == null ? undefined : choices.find(p => p.id === remembered)
  if (last) return last
  if (choices.length === 1) return choices[0]!
  const reached = choices.filter(p => p.relayOnline)
  return reached.length === 1 ? reached[0]! : null
}

/** "Relay reaches it" or "No relay", for the line beside a printer's name. */
export function relayLabel(p: Pick<PrintChoice, 'relayOnline'>): string {
  return p.relayOnline ? 'Relay Reaches It' : 'No Relay'
}

// ── The user's Printing ───────────────────────────────────────────────────────

/** A user's Printing (the API's `printing`): where they may print. */
export type Printing = 'SiteAndDevice' | 'DeviceOnly' | 'SiteOnly'

/** The three choices on the user's window, in order, each with its one-line hint. */
export const PRINTING_CHOICES: readonly { value: Printing, label: string, hint: string }[] = [
  { value: 'SiteAndDevice', label: 'Site Printers and This Device', hint: 'Both are offered; the one they used last is the main button.' },
  { value: 'DeviceOnly', label: 'This Device Only', hint: 'Print opens this device\'s own print dialog; the site\'s printers are never shown. For partners and anyone off the site\'s network.' },
  { value: 'SiteOnly', label: 'Site Printers Only', hint: 'Only the site\'s printers, through a print relay; no Use Local Printer. For floor PCs and shared logins.' },
]

/** The user's Printing as the API sent it; both ways when it is missing (an older answer). */
export function readPrinting(value: unknown): Printing {
  return value === 'DeviceOnly' || value === 'SiteOnly' ? value : 'SiteAndDevice'
}

/** The way a Print went last: to a site printer, or this device's own print. */
export type PrintWay = 'site' | 'here'

/** Where the way used last is kept, one per kind per browser (beside the printer used last). */
export const PRINT_WAY_KEYS: Record<PrinterKind, string> = {
  Label: 'bob.labelPrintWay',
  Paper: 'bob.paperPrintWay',
}

/** The way used last for this kind on this browser; null when none, or storage is closed. */
export function rememberedWay(kind: PrinterKind, storage: StorageLike | null = browserStorage()): PrintWay | null {
  try {
    const way = storage?.getItem(PRINT_WAY_KEYS[kind])
    return way === 'here' || way === 'site' ? way : null
  } catch {
    return null
  }
}

/** Keeps the way used, so the next Print makes it the main button. */
export function rememberWay(kind: PrinterKind, way: PrintWay, storage: StorageLike | null = browserStorage()): void {
  try { storage?.setItem(PRINT_WAY_KEYS[kind], way) } catch { /* the site printer stays first */ }
}

/** Which Print buttons a window shows, and which is the main one (the other is second). */
export type PrintButtons = { site: boolean, here: boolean, main: PrintWay }

/**
 * The Print buttons for a user's Printing: This Device Only prints here and never shows the site's
 * printers; Site Printers Only never prints here; with both, Use Local Printer is always
 * there, and is the main button when it was the way used last for this kind on this browser.
 * `hereAvailable` is false where the screen has no print of its own.
 */
export function printButtons(printing: Printing, hereAvailable: boolean, lastWay: PrintWay | null): PrintButtons {
  if (printing === 'DeviceOnly') return { site: false, here: hereAvailable, main: 'here' }
  if (printing === 'SiteOnly' || !hereAvailable) return { site: true, here: false, main: 'site' }
  return { site: true, here: true, main: lastWay === 'here' ? 'here' : 'site' }
}

/** Use Local Printer is offered to everyone but a Site Printers Only user. */
export function offersPrintHere(printing: Printing): boolean {
  return printing !== 'SiteOnly'
}

/** Whom a Site Printers Only user asks when no relay reaches the printer. */
export const ASK_ABOUT_THE_RELAY = 'Ask a manager to check the site\'s print relay (Admin > Printers, Print Relays).'

/**
 * Why a job cannot go to this printer through a relay now; null when it can. Print is then greyed
 * with it; with Use Local Printer, that prints this one job; without it, whom to ask.
 */
export function noRelayReason(p: PrintChoice | null, printing: Printing = 'SiteAndDevice'): string | null {
  if (!p) return null
  if (p.relayOnline) return null
  const why = `No print relay reaches ${p.name} right now, so BOB cannot print on it.`
  return printing === 'SiteOnly' ? `${why} ${ASK_ABOUT_THE_RELAY}` : why
}

export type FollowOutcome = 'printed' | 'failed' | 'cancelled' | 'waiting' | 'unsent'

/** Where a job stands in words, for the line under the printer and the notice. */
export function jobMessage(outcome: FollowOutcome | 'sent', printerName: string, job?: Pick<PrintJobStateDto, 'lastError'> | null): string {
  switch (outcome) {
    case 'sent': return `Sent to ${printerName}.`
    case 'printed': return `Printed on ${printerName}.`
    case 'cancelled': return `The job for ${printerName} was cancelled.`
    case 'waiting': return `Not printed yet: the job waits for a print relay that reaches ${printerName}.`
    case 'unsent': return `Not sent to ${printerName}.`
    case 'failed': {
      const why = job?.lastError?.trim()
      return why ? `Not printed on ${printerName}: ${why}` : `Not printed on ${printerName}.`
    }
  }
}

export interface FollowOptions {
  /** Reads the job's state (GET api/print-jobs/{id}). */
  get: (id: string) => Promise<PrintJobStateDto>
  /** Every 2 seconds. */
  intervalMs?: number
  /** For up to 60 seconds. */
  limitMs?: number
  wait?: (ms: number) => Promise<void>
  now?: () => number
  /** Each state read, for the screen's line. */
  onState?: (job: PrintJobStateDto) => void
  /** True once the person gave up on it (the window closed is not that: it is still followed). */
  stopped?: () => boolean
}

/**
 * Follows a job until it is printed, failed or cancelled, or the time is up ("waiting": it stays
 * queued for a relay). A read that fails is tried again on the next tick.
 */
export async function followJob(first: PrintJobStateDto, options: FollowOptions): Promise<{ outcome: FollowOutcome, job: PrintJobStateDto }> {
  const interval = options.intervalMs ?? 2_000
  const limit = options.limitMs ?? 60_000
  const wait = options.wait ?? ((ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)))
  const now = options.now ?? (() => Date.now())
  const until = now() + limit
  let job = first
  for (;;) {
    const settled = settledOutcome(job)
    if (settled) return { outcome: settled, job }
    if (options.stopped?.() || now() >= until) return { outcome: 'waiting', job }
    await wait(interval)
    try {
      job = await options.get(String(job.id))
      options.onState?.(job)
    } catch { /* tried again on the next tick */ }
  }
}

function settledOutcome(job: PrintJobStateDto): FollowOutcome | null {
  if (job.state === 'Printed') return 'printed'
  if (job.state === 'Failed') return 'failed'
  if (job.state === 'Cancelled') return 'cancelled'
  return null
}

/**
 * A page as the relay prints it: whole and on its own, with no scripts (the relay prints with
 * JavaScript off, as the tablet app does). The page's own styles, its @page size and orientation
 * among them, are left as they are.
 */
export function relayHtml(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<script\b[^>]*\/?>/gi, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
}

/** The addresses of the pictures a page shows that are not already inside it (data: URIs). */
export function outsideImages(html: string): string[] {
  const found = new Set<string>()
  for (const m of html.matchAll(/<img\b[^>]*?\ssrc\s*=\s*("([^"]*)"|'([^']*)')/gi)) {
    const src = (m[2] ?? m[3] ?? '').replace(/&amp;/g, '&').trim()
    if (src && !/^data:/i.test(src)) found.add(src)
  }
  return [...found]
}

/**
 * The page with each picture put inside it as a data: URI, so the relay prints it without reaching
 * BOB or the photo store. A picture that cannot be read is left as it was.
 */
export async function inlineImages(html: string, read: (src: string) => Promise<string | null>): Promise<string> {
  const sources = outsideImages(html)
  if (sources.length === 0) return html
  const inside = new Map<string, string>()
  await Promise.all(sources.map(async (src) => {
    try {
      const data = await read(src)
      if (data) inside.set(src, data)
    } catch { /* left as it was */ }
  }))
  return html.replace(/(<img\b[^>]*?\ssrc\s*=\s*)("([^"]*)"|'([^']*)')/gi, (whole, head: string, _q: string, dq?: string, sq?: string) => {
    const src = (dq ?? sq ?? '').replace(/&amp;/g, '&').trim()
    const data = inside.get(src)
    return data ? `${head}"${data}"` : whole
  })
}

/** Bytes as base64, for a PDF job. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  return btoa(binary)
}

// ── The Printers page's Print Relays ──────────────────────────────────────────

type RelayLike = {
  relayId?: string
  state?: 'Pending' | 'Approved' | 'Revoked'
  online?: boolean
  printers?: { printerId?: number, recent?: boolean }[]
}

/**
 * The relays that reach a printer now: approved, online, and reached it in the last two minutes,
 * as the API decides where a job may go. Their Relay IDs, for "Relay: FPO-RLY-01".
 */
export function relaysReaching(relays: readonly RelayLike[], printerId: number): string[] {
  return relays
    .filter(r => r.state === 'Approved' && r.online && (r.printers ?? []).some(p => p.printerId === printerId && p.recent))
    .map(r => r.relayId ?? '')
    .filter(Boolean)
}

/** "Relay: FPO-RLY-01" (or two), or "No relay". */
export function relayReachText(relays: readonly RelayLike[], printerId: number): string {
  const ids = relaysReaching(relays, printerId)
  return ids.length ? `Relay: ${ids.join(', ')}` : 'No relay'
}

/** "Seen 2 min ago", "Seen 3 hours ago", "Never seen". */
export function seenText(at: string | null | undefined, nowMs: number): string {
  if (!at) return 'Never seen'
  const minutes = Math.max(0, Math.floor((nowMs - new Date(at).getTime()) / 60_000))
  if (minutes < 1) return 'Seen just now'
  if (minutes < 120) return `Seen ${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `Seen ${hours} hours ago`
  return `Seen ${Math.floor(hours / 24)} days ago`
}

/** The Last Seen column's words: "Just now", "5 min ago", "Never". */
export function lastSeenText(at: string | null | undefined, nowMs: number): string {
  const said = seenText(at, nowMs)
  if (said === 'Never seen') return 'Never'
  const rest = said.replace(/^Seen /, '')
  return rest.charAt(0).toUpperCase() + rest.slice(1)
}

/** "From 203.0.113.7": the internet address a relay's last join came from, for its Machine column. */
export function joinedFromText(address: string | null | undefined): string {
  return address ? `From ${address}` : 'From an unknown address'
}

type WhereFrom = { relayId?: string, machineName?: string | null, network?: string | null, joinedFromAddress?: string | null }

/**
 * The one question Approve asks on the row (Mark, 2026-10-28: no join code), naming where the
 * request came from: "Approve FPO-RLY-03 on FPO-GATE-PC2, network 10.20.5.0/24, from 203.0.113.7?"
 */
export function approveQuestion(r: WhereFrom): string {
  // Short, as the row beside it already shows the PC and its network: the relay and where it asked from.
  return `Approve ${r.relayId ?? 'this relay'} ${r.joinedFromAddress ? `from ${r.joinedFromAddress}` : 'from an unknown address'}?`
}

/** Relays May Join From as the box holds it: one address or range a line. */
export function joinFromText(entries: readonly string[] | null | undefined): string {
  return (entries ?? []).join('\n')
}

/** What was typed in Relays May Join From, as the API takes it: one entry a line (commas too), trimmed, no blanks. */
export function joinFromEntries(text: string): string[] {
  return text.split(/[\r\n,]+/).map(line => line.trim()).filter(Boolean)
}
